/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\HotDeskingDeviceInfoBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Extensions module.
----------------------------------------------------------------*/


import { Phone } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import SecretField from '../../../components/ui/SecretField';
import Toggle from '../../../components/ui/Toggle';
import SelectSipProfile from '../../../components/ui/SelectSipProfile';
import SelectDispatchLocation from '../../../components/ui/SelectDispatchLocation';
import type { DispatchLocationOption } from '../../../components/ui/SelectDispatchLocation';
import { CodecField } from '../../../components/ui/CodecField';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';
import { LanguageSelector } from '../../extensions/components/LanguageSelector';
import { useI18n } from '../../../hooks/useI18n';
import { DEFAULT_LANGUAGE_CODE } from '../../../data/formOptions';
import { DeviceSipProfile } from '../../../services/deviceSipProfiles';
import type { HotDeskFormState } from '../types';

const DEFAULT_MAX_REGISTRATIONS = '5';
const DEFAULT_CODECS = 'PCMU,PCMA';
const SELECT_CLS = 'w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

interface Props {
    formData: HotDeskFormState;
    errors: Record<string, string>;
    isEditMode: boolean;
    isLoadingOptions: boolean;
    sipProfiles: DeviceSipProfile[];
    dispatchLocations: DispatchLocationOption[];
    buildTooltipContent: (tooltipKey: string, debugKey?: string) => string;
    onChange: <K extends keyof HotDeskFormState>(field: K, value: HotDeskFormState[K]) => void;
    onGeneratePassword: () => void;
}

function FieldLabel({ id, labelKey, tooltipKey, required, debugKey, buildTooltip, t }:
    { id: string; labelKey: string; tooltipKey: string; required?: boolean; debugKey?: string; buildTooltip: (k: string, d?: string) => string; t: (k: string) => string }) {
    return (
        <SimpleTooltip content={buildTooltip(tooltipKey, debugKey)} position="top" maxWidth="300px">
            <Label htmlFor={id} className="text-left" required={required}>{t(labelKey)}</Label>
        </SimpleTooltip>
    );
}

export function HotDeskingDeviceInfoBox({
    formData, errors, isEditMode, isLoadingOptions,
    sipProfiles, dispatchLocations, buildTooltipContent, onChange, onGeneratePassword,
}: Props) {
    const { t } = useI18n();
    const lbl = (id: string, labelKey: string, tooltipKey: string, required?: boolean, debugKey?: string) => (
        <FieldLabel id={id} labelKey={labelKey} tooltipKey={tooltipKey} required={required} debugKey={debugKey} buildTooltip={buildTooltipContent} t={t} />
    );

    return (
        <FormBox title={t('hotDesking.sections.deviceInfo')} icon={<Phone className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                {lbl('username', 'hotDesking.fields.username.label', 'hotDesking.fields.username.tooltip', true, 'extensions.debug.user_id')}
                <Input id="username" value={formData.username} onChange={(e) => onChange('username', e.target.value)} placeholder={t('hotDesking.fields.username.placeholder')} error={errors.username} disabled={isEditMode} />

                {lbl('description', 'hotDesking.fields.description.label', 'hotDesking.fields.description.tooltip')}
                <Input id="description" value={formData.description} onChange={(e) => onChange('description', e.target.value)} placeholder={t('hotDesking.fields.description.placeholder')} />

                {lbl('password', 'hotDesking.fields.password.label', 'hotDesking.fields.password.tooltip', true, 'extensions.debug.password')}
                <SecretField id="password" type="input" value={formData.password} onChange={(v) => onChange('password', v)} placeholder={t('hotDesking.fields.password.placeholder')} error={errors.password} showGenerate={true} onGenerate={onGeneratePassword} />

                {lbl('sipProfile', 'hotDesking.fields.sipProfile.label', 'hotDesking.fields.sipProfile.tooltip')}
                <SelectSipProfile value={formData.sipProfileId} onChange={(v) => onChange('sipProfileId', v)} sipProfiles={sipProfiles} placeholder={t('hotDesking.fields.sipProfile.placeholder')} disabled={isLoadingOptions} />

                {lbl('dispatchLocationId', 'extensions.fields.dispatchLocation.label', 'extensions.fields.dispatchLocation.tooltip')}
                <SelectDispatchLocation value={formData.dispatchLocationId ?? null} onChange={(v) => onChange('dispatchLocationId', v)} locations={dispatchLocations} placeholder={t('extensions.fields.dispatchLocation.placeholder', 'Select E911 location...')} disabled={isLoadingOptions} />

                {lbl('maxRegistrations', 'hotDesking.fields.maxRegistrations.label', 'hotDesking.fields.maxRegistrations.tooltip', false, 'extensions.debug.max_registrations')}
                <Input id="maxRegistrations" type="number" value={formData.maxRegistrations} onChange={(e) => onChange('maxRegistrations', e.target.value)} placeholder={DEFAULT_MAX_REGISTRATIONS} />

                {lbl('dtmfMode', 'hotDesking.fields.dtmfMode.label', 'hotDesking.fields.dtmfMode.tooltip', false, 'extensions.debug.dtmf_mode')}
                <select id="dtmfMode" value={formData.dtmfMode} onChange={(e) => onChange('dtmfMode', e.target.value)} className={SELECT_CLS}>
                    <option value="rfc4733">RFC 4733</option>
                    <option value="inband">Inband</option>
                    <option value="info">SIP INFO</option>
                    <option value="auto">Auto</option>
                </select>

                {lbl('language', 'extensions.fields.languageCode.label', 'extensions.fields.languageCode.tooltip', false, 'extensions.debug.default_language')}
                <LanguageSelector value={formData.language || DEFAULT_LANGUAGE_CODE} onChange={(v) => onChange('language', v)} showVoiceSelector={false} showLabel={false} />

                {lbl('inboundCodecs', 'hotDesking.fields.inboundCodecs.label', 'hotDesking.fields.inboundCodecs.tooltip', false, 'extensions.debug.inbound_codec_prefs')}
                <CodecField value={formData.inboundCodecs || DEFAULT_CODECS} onChange={(v) => onChange('inboundCodecs', v)} placeholder={t('hotDesking.fields.inboundCodecs.placeholder')} />

                {lbl('outboundCodecs', 'hotDesking.fields.outboundCodecs.label', 'hotDesking.fields.outboundCodecs.tooltip', false, 'extensions.debug.outbound_codec_prefs')}
                <CodecField value={formData.outboundCodecs || DEFAULT_CODECS} onChange={(v) => onChange('outboundCodecs', v)} placeholder={t('hotDesking.fields.outboundCodecs.placeholder')} />

                {lbl('enabled', 'hotDesking.fields.enabled.label', 'hotDesking.fields.enabled.tooltip', false, 'extensions.debug.enabled')}
                <div><Toggle id="enabled" value={formData.enabled} onChange={(v) => onChange('enabled', v)} /></div>
            </div>
        </FormBox>
    );
}
